import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import Axios from "@/lib/axios-instance";

// Define the response type
interface CreateSubscriptionResponse {
  meta: {
    error: boolean;
    code: number;
    message: string;
  };
  data: {
    checkout_url: string;
    session_id: string;
    plan_name: string;
    plan_price: number;
    billing_interval: string;
  } | null; // Allow data to be null for error responses
}

interface CreateSubscriptionParams {
  store_id: string;
  plan_id: string;
}

export const createSubscription = async ({ store_id, plan_id }: CreateSubscriptionParams): Promise<CreateSubscriptionResponse> => {
  try {
    const response = await Axios.post(`/customer-subscription/subscribe?store_id=${store_id}&plan_id=${plan_id}`);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      // Return the error response to handle it properly in the UI
      return error.response.data;
    }
    throw new Error(error.message || "Network Error");
  }
};

export const useCreateSubscription = () => {
  return useMutation({
    mutationFn: createSubscription,
    onSuccess: (response) => {
      // Check if there's an error in the response
      if (response.meta.error) {
        // Handle different error codes
        if (response.meta.code === 400 && response.meta.message.includes("already has an active subscription")) {
          toast.error("Subscription Error", {
            description: "Your store already has an active subscription. Please cancel it first or wait for it to expire."
          });
        } else {
          toast.error("Error", {
            description: response.meta.message || "An error occurred while creating subscription"
          });
        }
      } else {
        // Redirect to the checkout URL only if no error
        if (response.data && response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      }
    },
    onError: (error: any) => {
      console.error("Error creating subscription:", error);
      toast.error("Error", {
        description: error.message || "An error occurred while processing your subscription"
      });
    },
  });
};